import './styles/index.scss';

import { ThemeProvider } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  hasVisibleModal,
  healInteractionState,
  isInteractionBlocked,
} from './utils/interactionRecovery';
import { installIosTouchClickFix } from './utils/iosTouchClickFix';
import { isSepaProcessing, isSubscriptionLocked } from './utils/subscriptionGate';
import { storageMissingDemoFlag, isDemoMode } from 'demo';
import { SignIn } from './Containers/Auth/containers';
import { getActiveSalon, getAuth, getSalonCity } from './Containers/Auth/store/selectors';
import PageWrapper from './Containers/Common/PageWrapper';
import Spinner from './Containers/Common/Spinner';
import { ReactComponent as CheckIcon } from 'assets/svg/settings.svg';
import { ROUTES } from './routes/constants';
import { PrivateRoute, PublicRoute } from './routes/containers';
import Error from './shared/Error/Error';
import Loader from './shared/Loader/Loader';
import muiTheme from './themes/muiTheme';
import useAuth from './utils/useAuth';
import { HomePage } from './Containers/Home/containers';
import SchedulerContainer from './Containers/Scheduler/containers/SchedulerContainer';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import GlobalModal from './Containers/Modals/containers/GlobalModal';
import GlobalToast from 'Containers/Toasts/containers/GlobalToast';
import { Shop } from './Containers/Shop';
import { Communications } from './Containers/Communications';
import {
  SalonManagementList,
  Services,
  Workers,
  WorkerWorkTime,
  Closures,
  ExtraWorkingTime,
  ServicesList,
} from './Containers/SalonManagement/containers';
import {
  ClientsList,
  SalonInfo,
  Security,
  SettingsList,
  SharingContainer,
  PaymentsContainer,
  PremiumUpgrade,
  ExpensesList,
} from './Containers/Settings/containers';
import NotificationsList from './Containers/Notifications/containers/NotificationsList';
import WorkersList from './Containers/SalonManagement/containers/Services/containers/WorkersList';
import ServiceIconsList from './Containers/SalonManagement/containers/Services/containers/ServiceIconsList';
import { StatisticNew } from './Containers/Statistic/containers';
import { ReviewsList } from './Containers/Reviews/containers';
import { AdvertisingHub } from './Containers/Advertising/containers';
import { RegistrationPageContainer } from './Containers/Auth/containers/RegistrationPage';
import { authActions } from './Containers/Auth/store';
import { settingsActions } from 'Containers/Settings/store';
import store from 'store/store';
import { getCustomerProducts, getMyAccountStaff, getProductsLoading, getShowTokenExpiredModal, getTokenExpiredModalReason } from 'Containers/Settings/store/selectors';
import ConfirmSecondaryModal from 'Containers/Common/ConfirmSecondaryModal';
import SubscriptionExpiryModal from 'Containers/Common/SubscriptionExpiryModal';
import PaymentList from 'Containers/Payments/containers/PaymentList';
import { noPriceSalons } from 'Containers/Scheduler/constants';
import BlankPage from 'Containers/Payments/containers/BlankPage';
import LinearLoader from 'Containers/Common/LinearLoader';
import StepRoles from 'Containers/Payments/containers/BlankPage/StepRoles';
import StepForm from 'Containers/Payments/containers/BlankPage/StepForm'
import InformationsPage from 'Containers/Payments/containers/BlankPage/InformationsPage';
import PrivateAppPage from 'Containers/PrivateApp';
import RecoveryPwdPage from 'Containers/Auth/containers/RecoveryPwdPage';
import ResetPwdPage from 'Containers/Auth/containers/ResetPwdPage';
import Blocker from 'Containers/Scheduler/containers/Blocker';
import Modal from 'Containers/Common/Modal';
import TermsModal from 'Containers/Modals/containers/TermsModal';
import LanguageSelectorModal from 'Containers/Common/LanguageSelectorModal';
import useWindowSize from 'utils/useWindowSize';
import { clientActions } from 'Containers/Clients/store';
import AccountsManager from 'Containers/Settings/containers/AccountsManager';
import OtpCredits from 'Containers/Settings/containers/OtpCredits';
import { sendMessageToApp } from 'utils/exsternalCall';
import SupportContainer from 'Containers/Settings/containers/SupportContainer';
import { syncHasShopStorage } from 'utils/hasShopAccess';
dayjs.locale(localStorage.getItem('i18nextLng') || 'it');
dayjs.extend(customParseFormat);

const SUBSCRIPTION_EXPIRY_DISMISSED_KEY = 'subscriptionExpiryDismissed';

const getLocalToday = () => dayjs().format('YYYY-MM-DD');

const isSubscriptionExpiryDismissedToday = (scadenza) => {
  try {
    const raw = localStorage.getItem(SUBSCRIPTION_EXPIRY_DISMISSED_KEY);
    if (!raw) return false;
    const dismissed = JSON.parse(raw);
    if (!dismissed?.day || dismissed.day !== getLocalToday()) return false;
    if (
      scadenza != null &&
      dismissed.scadenza != null &&
      String(dismissed.scadenza) !== String(scadenza)
    ) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

const dismissSubscriptionExpiryForToday = (scadenza) => {
  localStorage.setItem(
    SUBSCRIPTION_EXPIRY_DISMISSED_KEY,
    JSON.stringify({
      day: getLocalToday(),
      scadenza: scadenza != null ? String(scadenza) : null,
    }),
  );
};

export default () => {
  const { t } = useTranslation();
  const { isLoading } = useAuth();

  const isAuthenticated = useSelector(getAuth());
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const adT = searchParams.get('adT');
  const fromApp = searchParams.get('fromApp');

  const user = useSelector(getActiveSalon());
  const token = useSelector(getAuth());
  const products = useSelector(getCustomerProducts());
  const productsLoading = useSelector(getProductsLoading());
  const salon = useSelector(getActiveSalon());
  const [paymentsDaysLeft, setPaymentsDaysLeft] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const {isMobile} = useWindowSize();
  const [termsModal, setTermsModal] = useState(false);
  const [languageModal, setLanguageModal] = useState(false);
  const myAccount = useSelector(getMyAccountStaff());
  const showTokenExpiredModal = useSelector(getShowTokenExpiredModal());
  const tokenExpiredModalReason = useSelector(getTokenExpiredModalReason());

  // setTimeout(() => {
  //   connectionSSE()
  // }, 2000)

  const updateBackground = () => {
    const interval = setInterval(()=>dispatch(settingsActions.GET_MY_ACCOUNT_STAFF.REQUEST()), 30 * 60 * 1000);
    return () => clearInterval(interval);
  }

  // Refresh salone: giornaliero, città mancante, oppure se in lista c’è ancora un flag demo
  // (serve per prendere demoMode 0 appena l’operatore lo toglie).
  useEffect(() => {
    if (!token || !isAuthenticated) return;

    const LAST_SALON_REFRESH_KEY = 'lastSalonRefresh';
    const SESSION_CITY_FETCH_KEY = 'salonCityFetchAttempted';
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;

    const lastRefresh = localStorage.getItem(LAST_SALON_REFRESH_KEY);
    const now = Date.now();
    const needsDailyRefresh =
      !lastRefresh || now - parseInt(lastRefresh, 10) >= ONE_DAY_MS;

    let missingCity = false;
    let missingDemoFlag = false;
    let hasDemoSalon = false;
    try {
      const storedSalons = JSON.parse(localStorage.getItem('salons') || '[]');
      missingCity =
        Array.isArray(storedSalons) &&
        storedSalons.some((salon) => salon && !getSalonCity(salon));
      missingDemoFlag = storageMissingDemoFlag();
      hasDemoSalon = Array.isArray(storedSalons) && storedSalons.some((salon) => isDemoMode(salon));
    } catch (_) {
      missingCity = false;
      missingDemoFlag = false;
      hasDemoSalon = false;
    }

    const needsCityRefresh =
      missingCity && !sessionStorage.getItem(SESSION_CITY_FETCH_KEY);

    if (needsDailyRefresh || needsCityRefresh || missingDemoFlag || hasDemoSalon) {
      dispatch(authActions.FETCH_SALON.REQUEST());
      if (needsDailyRefresh) {
        localStorage.setItem(LAST_SALON_REFRESH_KEY, now.toString());
      }
      if (needsCityRefresh) {
        sessionStorage.setItem(SESSION_CITY_FETCH_KEY, '1');
      }
    }
  }, [token, isAuthenticated, dispatch]);

  useEffect(() => {
    if (adT) {
      localStorage.removeItem('salons');
      localStorage.removeItem('home_stat_cache');
      localStorage.removeItem('statSalone');
      localStorage.removeItem('lastSalonRefresh');
      dispatch(authActions.LOG_OUT.SUCCESS());
      
      dispatch(authActions.SIGN_IN.REQUEST({ token: adT }), () => {
        window.location.href = ROUTES.HOME;
        window.location.reload();
      });
    }
  }, [adT]);
  // Bootstrap abbonamento: una sola volta al login (token).
  // Cambio salone: lo fa già setActiveSalon saga → evita doppio getDaysLeft.
  useEffect(() => {
    if (!token) return;
    const hasProducts = Boolean(products && Object.keys(products).length);
    if (!hasProducts && !productsLoading) {
      dispatch(
        settingsActions.GET_CUSTOMER_PRODUCTS.REQUEST(
          { clientId: user?.idCostumerStripe },
          null,
          { skipLoader: true },
        ),
      );
    }
  }, [token, dispatch, products, productsLoading, user?.idCostumerStripe]);

  useEffect(() => {
    if (!token) return;
    dispatch(clientActions.GET_BAD_CLIENTS.REQUEST({}, null, { skipLoader: true }));
    if (!localStorage.getItem('salons')) setTermsModal(false);
    if (localStorage.getItem('staff')) {
      dispatch(settingsActions.GET_MY_ACCOUNT_STAFF.REQUEST({}, null, { skipLoader: true }));
      updateBackground();
    }
  }, [token, user?.idsalone]);

  useEffect(() => {
    if (token && user?.idCostumerStripe) {
      dispatch(settingsActions.GET_CUSTOMER_INVOICE.REQUEST({ clientId: user.idCostumerStripe }, null, { skipLoader: true }));
    }
  }, [token, user?.idCostumerStripe, dispatch]);

  useEffect(() => {
    if (searchParams.get('previewExpiry') === '1') {
      setPaymentsDaysLeft(true);
    } else if (products && products.giorniRimanenti <= 3 && !isSepaProcessing(products)) {
      // Con RID in elaborazione il rinnovo è già partito: niente modal "in scadenza"
      if (!isSubscriptionExpiryDismissedToday(products.scadenza)) {
        setPaymentsDaysLeft(true);
      }
    } else if (products && products.giorniRimanenti > 3) {
      localStorage.removeItem(SUBSCRIPTION_EXPIRY_DISMISSED_KEY);
    }
    if (products && products.acceptedTerms === false && localStorage.getItem('salons')) setTermsModal(true)
    else setTermsModal(false);
  
    if(products && products.badge === 1) localStorage.setItem('has_badge', true);
    else localStorage.setItem('has_badge', false);
    syncHasShopStorage(products);
  }, [products, searchParams]);

  // Mostra il modal della lingua se l'utente è autenticato ma non ha la lingua salvata
  useEffect(() => {
    if (isAuthenticated && !localStorage.getItem('i18nextLng')) {
      setLanguageModal(true);
    }
  }, [isAuthenticated]);
  useEffect(() => {
      if (localStorage.getItem('fromApp') || window.__ZB_LIGHT_CLIENT__) {
        return undefined;
      }
      const interval = setInterval(() => {
        if (window._hsq) {
          window._hsq.push([
            "identify",
            {
              idsalone: salon.idsalone,
              email: salon.emailVerificata || salon.email,
              phone: salon.cell,
              firstname: salon.nome_salone,
            },
          ]);
          clearInterval(interval);
        }
      }, 300);
    
      return () => clearInterval(interval);
  }, [salon, token]);
  useEffect(() => {
    if (fromApp) localStorage.setItem('fromApp', fromApp);
  }, [fromApp]);

  // iOS: touch e click possono divergere (ghost click su elementi sotto).
  useEffect(() => installIosTouchClickFix(), []);

  // Sblocca tap/freeze residui dopo cambio route o ritorno in foreground.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (!hasVisibleModal()) healInteractionState({ reason: `route:${location.pathname}` });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !hasVisibleModal()) {
        healInteractionState({ reason: 'visibilitychange' });
      }
    };
    const onPageShow = () => {
      if (!hasVisibleModal()) healInteractionState({ reason: 'pageshow' });
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return undefined;
    const timer = window.setInterval(() => {
      if (isInteractionBlocked() && !document.querySelector('[data-modal-open="true"]')) {
        // eslint-disable-next-line no-console
        console.warn('[interactionRecovery] blocked without modal — heal');
        healInteractionState({ reason: 'dev-watchdog' });
      }
    }, 3000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    sendMessageToApp('SALONS', { data: localStorage.getItem('salons') });
  }, [salon?.idsalone]);

  const isDemoPaymentAvailable =
    isDemoMode(salon) ||
    (process.env.REACT_APP_PAYMENT_DEMO == 'true' && salon?.email == 'demo@demo.it');
  // Scaduto → solo Fatturazione. Eccezione: RID/SEPA ancora "processing"
  // (conferma bancaria 7–15gg) → nessun blocco entro la grace (vedi utils/subscriptionGate).
  const locked = isSubscriptionLocked(products);
  // Schermo dedicata: #root è overflow:hidden → serve wrapper scrollabile
  if (!isDemoPaymentAvailable && locked) {
    return (
      <div
        style={{
          boxSizing: 'border-box',
          width: '100%',
          height: '100dvh',
          maxHeight: '100dvh',
          overflowX: 'hidden',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
        }}
      >
        <PaymentsContainer />
      </div>
    );
  }
  const page = window.location.pathname;

  return (
    <>
      {!isLoading ? (
        <ThemeProvider theme={muiTheme}>
          <Loader />
          <Error />
          <GlobalToast />
          <SubscriptionExpiryModal
            isOpen={paymentsDaysLeft}
            onClose={() => {
              dismissSubscriptionExpiryForToday(products?.scadenza);
              setPaymentsDaysLeft(false);
            }}
            expiryDate={products?.scadenza}
          />
          <ConfirmSecondaryModal
            isModalOpen={showTokenExpiredModal}
            setIsModalOpen={() => dispatch(settingsActions.HIDE_TOKEN_EXPIRED_MODAL.SUCCESS())}
            title={
              tokenExpiredModalReason === 'salonsList'
                ? t('modals.salonsListRefresh.title')
                : t('modals.tokenExpired.title')
            }
            subtitle={
              tokenExpiredModalReason === 'salonsList'
                ? t('modals.salonsListRefresh.subtitle')
                : t('modals.tokenExpired.subtitle')
            }
            buttons={[t('modals.tokenExpired.buttons.loginAgain')]}
            Icon={CheckIcon}
            buttonsActions={[
              () => {
                dispatch(settingsActions.HIDE_TOKEN_EXPIRED_MODAL.SUCCESS());
                dispatch(authActions.LOG_OUT.REQUEST());
              },
            ]}
          />
          <Modal
            isModalOpen={termsModal}
            setIsModalOpen={setTermsModal}
            centered
            height="auto"
            maxHeight="90vh"
            width={isMobile ? '100%' : '420px'}
            maxWidth={isMobile ? '100%' : '420px'}
            borderRadius={isMobile ? '20px' : '16px'}
            closeOnOverlayClick={false}
          >
            <TermsModal />
          </Modal>
          <main className='container'>
            <Routes>
              <Route
                path={ROUTES.SIGN_IN}
                element={
                  <PublicRoute
                    component={SignIn}
                    isAuthenticated={isAuthenticated}
                    restricted={true}
                  />
                }
              />
              <Route
                path={`${ROUTES.SIGN_IN}/welcome`}
                element={
                  <PublicRoute
                    component={RegistrationPageContainer}
                    isAuthenticated={isAuthenticated}
                    restricted={true}
                  />
                }
              />
              <Route
                element={
                  <PageWrapper>
                    <Outlet />
                  </PageWrapper>
                }
              >
                <Route path={ROUTES.HOME} element={<Outlet />}>
                  <Route
                    index
                    element={
                      <PrivateRoute component={HomePage} isAuthenticated={isAuthenticated} />
                    }
                  />
                </Route>
                <Route path={ROUTES.SCHEDULER} element={<Outlet />}>
                  <Route
                    index
                    element={
                      <PrivateRoute
                        component={products.hasPricesZero && noPriceSalons.includes(salon.idsalone) ?  Blocker : SchedulerContainer}
                        isAuthenticated={isAuthenticated}
                      />
                    }
                  />
                </Route>
                <Route path={ROUTES.PAYMENTS} element={<Outlet />}>
                  <Route
                    index
                    element={
                      <PrivateRoute component={BlankPage} isAuthenticated={isAuthenticated} />
                    }
                  />
                  <Route
                    path={`${ROUTES.PAYMENTS}${ROUTES.INFORMATIONS}`}
                    element={
                      <PrivateRoute component={InformationsPage} isAuthenticated={isAuthenticated} />
                    }
                  />
                  <Route
                    path={`${ROUTES.PAYMENTS}${ROUTES.STEPS}`}
                    element={
                      <PrivateRoute component={StepRoles} isAuthenticated={isAuthenticated} />
                    }
                  />
                  <Route
                    path={`${ROUTES.PAYMENTS}${ROUTES.FORM}`}
                    element={
                      <PrivateRoute component={StepForm} isAuthenticated={isAuthenticated} />
                    }
                  />
                </Route>
                <Route
                  path={ROUTES.PRIVATE_APP}
                  element={
                    <PrivateRoute component={PrivateAppPage} isAuthenticated={isAuthenticated} />
                  }
                />
                <Route path={ROUTES.SALON_MANAGEMENT} element={<Outlet />}>
                  <Route
                    index
                    element={
                      <PrivateRoute
                        component={SalonManagementList}
                        isAuthenticated={isAuthenticated}
                      />
                    }
                  />
                  <Route
                    path={`${ROUTES.SALON_MANAGEMENT}${ROUTES.SERVICES}`}
                    element={
                      <PrivateRoute component={Services} isAuthenticated={isAuthenticated} />
                    }
                  />
                  <Route
                    path={`${ROUTES.SALON_MANAGEMENT}${ROUTES.WORKERS}`}
                    element={<PrivateRoute component={Workers} isAuthenticated={isAuthenticated} />}
                  />
                  <Route
                    path={`${ROUTES.SALON_MANAGEMENT}${ROUTES.WORKERS}${ROUTES.SERVICES}/:workerId`}
                    element={
                      <PrivateRoute component={ServicesList} isAuthenticated={isAuthenticated} />
                    }
                  />
                  <Route
                    path={`${ROUTES.SALON_MANAGEMENT}${ROUTES.WORKERS}${ROUTES.WORKING_TIME}/:workerId`}
                    element={
                      <PrivateRoute component={WorkerWorkTime} isAuthenticated={isAuthenticated} />
                    }
                  />
                  <Route
                    path={`${ROUTES.SALON_MANAGEMENT}${ROUTES.WORKERS}${ROUTES.CLOSURES}/:workerId`}
                    element={
                      <PrivateRoute component={Closures} isAuthenticated={isAuthenticated} />
                    }
                  />
                  <Route
                    path={`${ROUTES.SALON_MANAGEMENT}${ROUTES.WORKERS}${ROUTES.EXTRA_WORKING_TIME}/:workerId`}
                    element={
                      <PrivateRoute
                        component={ExtraWorkingTime}
                        isAuthenticated={isAuthenticated}
                      />
                    }
                  />
                  <Route
                    path={`${ROUTES.SALON_MANAGEMENT}${ROUTES.SERVICES}${ROUTES.WORKERS}/:serviceId`}
                    element={
                      <PrivateRoute component={WorkersList} isAuthenticated={isAuthenticated} />
                    }
                  />
                  <Route
                    path={`${ROUTES.SALON_MANAGEMENT}${ROUTES.SERVICES}${ROUTES.ICONS}/:serviceId`}
                    element={
                      <PrivateRoute
                        component={ServiceIconsList}
                        isAuthenticated={isAuthenticated}
                      />
                    }
                  />
                </Route>

                <Route path={ROUTES.COMMUNICATIONS} element={<Outlet />}>
                  <Route
                    index
                    element={
                      <PrivateRoute component={Communications} isAuthenticated={isAuthenticated} />
                    }
                  />
                </Route>
                <Route path={ROUTES.NOTIFICATION} element={<Outlet />}>
                  <Route
                    index
                    element={
                      <PrivateRoute
                        component={NotificationsList}
                        isAuthenticated={isAuthenticated}
                      />
                    }
                  />
                </Route>

                <Route path={ROUTES.STATISTICS} element={<Outlet />}>
                  <Route
                    index
                    element={
                      <PrivateRoute
                        component={StatisticNew}
                        isAuthenticated={isAuthenticated}
                      />
                    }
                  />
                </Route>
                <Route
                  path="/statistiche_new"
                  element={<Navigate to={ROUTES.STATISTICS} replace />}
                />
                <Route path={ROUTES.SHOP} element={<Outlet />}>
                  <Route
                    index
                    element={<PrivateRoute component={Shop} isAuthenticated={isAuthenticated} />}
                  />
                </Route>
                <Route path={ROUTES.ARTICLES} element={<Outlet />}>
                  <Route
                    index
                    element={
                      <PrivateRoute
                        component={() => <div>ARTICLES</div>}
                        isAuthenticated={isAuthenticated}
                      />
                    }
                  />
                </Route>
                <Route
                  path={`${ROUTES.CLIENTS}`}
                  element={
                    <PrivateRoute component={ClientsList} isAuthenticated={isAuthenticated} />
                  }
                />
                <Route path={ROUTES.REVIEWS} element={<Outlet />}>
                  <Route
                    index
                    element={
                      <PrivateRoute
                        component={ReviewsList}
                        isAuthenticated={isAuthenticated}
                      />
                    }
                  />
                </Route>
                <Route path={ROUTES.ADVERTISING} element={<Outlet />}>
                  <Route
                    index
                    element={
                      <PrivateRoute
                        component={AdvertisingHub}
                        isAuthenticated={isAuthenticated}
                      />
                    }
                  />
                </Route>
                <Route path={ROUTES.SETTINGS} element={<Outlet />}>
                  <Route
                    index
                    element={
                      <PrivateRoute component={SettingsList} isAuthenticated={isAuthenticated} />
                    }
                  />
                  <Route
                    path={`${ROUTES.SETTINGS}${ROUTES.SALONE}`}
                    element={
                      <PrivateRoute component={SalonInfo} isAuthenticated={isAuthenticated} />
                    }
                  />
                  <Route
                    path={`${ROUTES.SETTINGS}${ROUTES.ACCOUNTS}`}
                    element={
                      <PrivateRoute component={AccountsManager} isAuthenticated={isAuthenticated} />
                    }
                  />
                  <Route
                    path={`${ROUTES.SETTINGS}${ROUTES.EXPENSES}`}
                    element={
                      <PrivateRoute component={ExpensesList} isAuthenticated={isAuthenticated} />
                    }
                  />
                  <Route
                    path={`${ROUTES.SETTINGS}${ROUTES.SECURITY}`}
                    element={
                      <PrivateRoute component={Security} isAuthenticated={isAuthenticated} />
                    }
                  />
                  <Route
                    path={`${ROUTES.SETTINGS}${ROUTES.PAYMENTS}`}
                    element={
                      <PrivateRoute
                        component={PaymentsContainer}
                        isAuthenticated={isAuthenticated}
                      />
                    }
                  />
                  <Route
                    path={`${ROUTES.SETTINGS}${ROUTES.OTP}`}
                    element={
                      <PrivateRoute component={OtpCredits} isAuthenticated={isAuthenticated} />
                    }
                  />
                  {/* Relativo `premium` + assoluto: alcuni WebView/RR nested matchano solo uno dei due. */}
                  <Route
                    path={ROUTES.PREMIUM.replace(/^\//, '')}
                    element={
                      <PrivateRoute
                        component={PremiumUpgrade}
                        isAuthenticated={isAuthenticated}
                      />
                    }
                  />
                  <Route
                    path={`${ROUTES.SETTINGS}${ROUTES.PREMIUM}`}
                    element={
                      <PrivateRoute
                        component={PremiumUpgrade}
                        isAuthenticated={isAuthenticated}
                      />
                    }
                  />
                  <Route path={`${ROUTES.SETTINGS}${ROUTES.SHARING}`} element={<Outlet />}>
                    <Route
                      index
                      element={
                        <PrivateRoute
                          component={SharingContainer}
                          isAuthenticated={isAuthenticated}
                        />
                      }
                    />
                  </Route>
                </Route>
                <Route
                  path={ROUTES.SUPPORT}
                  element={
                    <PrivateRoute component={SupportContainer} isAuthenticated={isAuthenticated} />
                  }
                />
              </Route>
              <Route
                path='*'
                element={
                  isAuthenticated ? <Navigate to={ROUTES.HOME} /> : <Navigate to={ROUTES.SIGN_IN} />
                }
              />
              <Route path={ROUTES.RECOVERY}  element={isAuthenticated ? <Navigate to={ROUTES.HOME} /> : <Outlet />}>
                <Route
                  index
                  element={
                    <PublicRoute
                      component={RecoveryPwdPage}
                      isAuthenticated={!isAuthenticated}
                    />
                  }
                />
                <Route
                  path={`${ROUTES.RECOVERY}/password`}
                  element={
                    isAuthenticated ? <Navigate to={ROUTES.HOME} /> 
                      : <PublicRoute
                        component={ResetPwdPage}
                        isAuthenticated={!isAuthenticated}
                      />
                  }
                />
              </Route>
            </Routes>
          </main>
          <GlobalModal />
          <LanguageSelectorModal
            isOpen={languageModal}
            onClose={() => setLanguageModal(false)}
          />
        </ThemeProvider>
      ) : page != ROUTES.SCHEDULER ? (
        <Spinner />
      ) : (
        <LinearLoader />
      )}
    </>
  );
};
